import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import * as React from 'react';

import { reactPlugin } from './reactPlugin';
import { ONETRUST_STATISTICS_GROUP_ID } from 'lib/helpers/constants';

declare global {
  interface WindowEventMap {
    OneTrustGroupsUpdated: CustomEvent<string[]>;
  }
  interface Window {
    OnetrustActiveGroups?: string;
  }
}

export interface AppInsightsConfig {
  connectionString: string | undefined;
  productName: string;
  productVersion: string;
}

async function initializeAppInsight(config: AppInsightsConfig) {
  if (!config.connectionString) {
    // do not attempt initialization if we don't have a connection string
    return;
  }

  const { connectionString, productName, productVersion } = config;

  try {
    const { ApplicationInsights } =
      await import('@microsoft/applicationinsights-web');

    const appInsights = new ApplicationInsights({
      config: {
        connectionString,
        disableCookiesUsage: true, // Disable cookies by default
        enableAutoRouteTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
        extensions: [reactPlugin],
      },
    });

    appInsights.addTelemetryInitializer((envelope) => {
      envelope.data = envelope.data || {};

      /**
       * NOTE: We depend on the assumption that our apps are hosted on subdomains of 'identity.zeiss.com'
       */
      const env = location.hostname.split('identity.zeiss.com')[0];
      envelope.data.environment = env === '' ? 'prod' : env.replace(/\.$/, '');
      envelope.data.productName = productName;
      envelope.data.productVersion = productVersion;
    });

    appInsights.addTelemetryInitializer(() => {
      const hasConsent =
        window.OnetrustActiveGroups &&
        window.OnetrustActiveGroups.includes(ONETRUST_STATISTICS_GROUP_ID);

      // Send or drop telemetry based on consent
      if (typeof hasConsent === 'boolean') {
        return hasConsent;
      }
      return false;
    });

    appInsights.loadAppInsights();
    return appInsights;
  } catch {
    console.log('Unable to load app insights');
    return;
  }
}

interface AppInsightsProviderProps {
  children: React.ReactNode;
  config: AppInsightsConfig;
}

export const AppInsightsProvider = ({
  children,
  config,
}: AppInsightsProviderProps) => {
  React.useEffect(() => {
    let appInsights: ApplicationInsights | undefined;

    initializeAppInsight(config).then((insights) => {
      appInsights = insights;
    });

    /**
     * Handle OneTrust consent changes
     */
    const handleConsentChange = (event: CustomEvent<string[]>) => {
      const hasConsent = event.detail.includes(ONETRUST_STATISTICS_GROUP_ID);

      if (!appInsights) return;

      if (hasConsent) {
        // enable cookies
        appInsights.core.getCookieMgr().setEnabled(true);
      } else {
        // disable cookies
        appInsights.core.getCookieMgr().setEnabled(false);

        // purge cookies
        appInsights.core.getCookieMgr().purge('ai_user');
        appInsights.core.getCookieMgr().purge('ai_session');

        // clear pending telemetry
        appInsights.flush();
      }
    };

    /**
     * Listen for OneTrust consent changes
     * https://developer.onetrust.com/onetrust/docs/javascript-events-guide
     */
    window.addEventListener('OneTrustGroupsUpdated', handleConsentChange);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', handleConsentChange);
    };
  }, []);

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      {children}
    </AppInsightsContext.Provider>
  );
};
